import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useSeo } from "../components/Seo";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", email: "", message: "" };

export default function Contact() {
    useSeo({
        title: "Contact Us — HALTONS.CA",
        description:
            "Contact HALTONS.CA, the independent civic information publication covering the 2026 mayoral elections in Milton, Oakville, Burlington and Halton Hills.",
        path: "/contact",
    });

    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, form);
            setSubmitted(true);
            setForm(initialForm);
            toast.success("Thank you — your message has been received.");
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main data-testid="contact-page">
            <section className="border-b border-border bg-navy text-ivory">
                <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6">
                    <h1 className="font-serif-display text-4xl font-bold sm:text-5xl">Contact Us</h1>
                    <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ivory/75 md:text-base">
                        Questions, tips or candidate information to share? Send us a message below. To
                        flag a factual error in anything we have published, please use our{" "}
                        <Link to="/corrections" data-testid="contact-corrections-link" className="font-semibold text-gold link-underline">
                            corrections form
                        </Link>{" "}
                        instead.
                    </p>
                </div>
            </section>

            <section className="mx-auto max-w-2xl px-4 py-14 sm:px-6">
                {submitted ? (
                    <Reveal>
                        <div className="border border-teal/40 bg-teal/5 p-8" data-testid="contact-success">
                            <h2 className="font-serif-display text-xl font-semibold text-navy">Thank you</h2>
                            <p className="mt-3 text-sm leading-relaxed text-foreground/90">
                                Your message has been received. We read every note that comes in.
                            </p>
                            <button
                                type="button"
                                data-testid="contact-another-button"
                                onClick={() => setSubmitted(false)}
                                className="mt-5 text-sm font-semibold text-teal link-underline"
                            >
                                Send another message
                            </button>
                        </div>
                    </Reveal>
                ) : (
                    <Reveal>
                        <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-5">
                            <div className="grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="contact-name" className="text-xs font-semibold uppercase tracking-widest text-navy">
                                        Name <span className="text-teal">*</span>
                                    </label>
                                    <input
                                        id="contact-name"
                                        data-testid="contact-name-input"
                                        type="text"
                                        required
                                        value={form.name}
                                        onChange={update("name")}
                                        className="mt-2 w-full border border-border bg-white px-4 py-3 text-sm text-navy outline-none focus:border-teal"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="contact-email" className="text-xs font-semibold uppercase tracking-widest text-navy">
                                        Email <span className="text-teal">*</span>
                                    </label>
                                    <input
                                        id="contact-email"
                                        data-testid="contact-email-input"
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={update("email")}
                                        className="mt-2 w-full border border-border bg-white px-4 py-3 text-sm text-navy outline-none focus:border-teal"
                                    />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="contact-message" className="text-xs font-semibold uppercase tracking-widest text-navy">
                                    Message <span className="text-teal">*</span>
                                </label>
                                <textarea
                                    id="contact-message"
                                    data-testid="contact-message-input"
                                    required
                                    rows={7}
                                    value={form.message}
                                    onChange={update("message")}
                                    placeholder="How can we help?"
                                    className="mt-2 w-full border border-border bg-white px-4 py-3 text-sm text-navy outline-none focus:border-teal"
                                />
                            </div>
                            <button
                                type="submit"
                                data-testid="contact-submit-button"
                                disabled={submitting}
                                className="bg-navy px-8 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-teal disabled:opacity-60"
                            >
                                {submitting ? "Sending…" : "Send message"}
                            </button>
                        </form>
                    </Reveal>
                )}
            </section>
        </main>
    );
}
